import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SerializedError } from '@reduxjs/toolkit'
import { BaseQueryError } from '@reduxjs/toolkit/dist/query/baseQueryTypes'

import { ConfigBarChartForm } from '@pages/DiagramsCreateOrEdit/types'

import { useFetchAllViewQuery } from '@redux/api'
import { useCopyBarChartMutation, useCreateOrUpdateBarChartMutation } from '@redux/api/barChart.api'

import { MODAL_TYPE, ROUTES } from '@constants'
import { CopyModelDataType, DataOfCopiedModelType } from '@types'

import { BarChartResponseType, OBJECT_TYPE, OPERATION_TYPE } from '../../../types/api/barChart.api'

type IProps = {
  fetchBarCharts: () => Promise<
    { data: BarChartResponseType[] } | { error: BaseQueryError<any> | SerializedError }
  > & {
    requestId: string
    abort: () => void
    unwrap: () => Promise<BarChartResponseType[]>
    reset: () => void
  }
}

export const useHandlers = ({ fetchBarCharts }: IProps) => {
  const navigate = useNavigate()

  const [copyDiagramData, setCopyDiagramData] = useState<ConfigBarChartForm | null>(null)
  const [diagrams, setDiagrams] = useState<BarChartResponseType[]>([])
  const [deleteId, setDeleteId] = useState<number>()
  const [copyId, setCopyId] = useState<number>()
  const [copyData, setCopyData] = useState<DataOfCopiedModelType>()

  const { data: views } = useFetchAllViewQuery()
  const [requestBarChart, { isLoading: isLoadingDelete }] = useCreateOrUpdateBarChartMutation()
  const [copyBarChart, { isLoading: isLoadingCopy }] = useCopyBarChartMutation()

  const handleCreateContext = () => navigate(MODAL_TYPE.CREATE)

  const handleEditDiagram = (id: number) => {
    navigate(ROUTES.DIAGRAMS_EDIT + `/${id}`)
  }

  const handleDeleteDiagram = () => {
    requestBarChart([
      {
        operationType: OPERATION_TYPE.DELETE,
        type: OBJECT_TYPE.DIAGRAM,
        data: {
          id: deleteId,
        },
      },
    ]).finally(() => {
      getData()
    })
    setDeleteId(undefined)
  }

  const getData = () => {
    fetchBarCharts()
      .unwrap()
      .then(data => setDiagrams(data))
  }

  useEffect(() => {
    getData()
  }, [])

  const handleShowDeleteModal = (id: number) => {
    setDeleteId(id)
  }

  const handleCloseDeleteModal = () => {
    setDeleteId(undefined)
  }

  const handleCopyDiagram = (data: CopyModelDataType) => {
    copyBarChart({
      id: data.copyId,
      code: data.code,
      title: data.title,
    }).then(() => getData())
    setCopyId(undefined)
  }

  const handleShowCopyDialog = (id: number, title: string) => {
    setCopyId(id)
    setCopyData({
      id,
      title,
    })
  }

  const handleCloseCopyDialog = () => {
    setCopyId(undefined)
  }

  const handleRowClick = ({ row }: { row: { id: number; code: string } }) => {
    navigate(`${ROUTES.DIAGRAMS}/${row.id}`)
  }

  return {
    state: {
      diagrams,
      showCopyDialog: Boolean(copyId),
      copyDiagramData,
      isLoadingCopy,
      copyData,
      isLoadingDelete,
      showDeleteModal: Boolean(deleteId),
    },
    handlers: {
      handleEditDiagram,
      handleDeleteDiagram,
      handleCreateContext,
      handleCloseDeleteModal,
      handleShowDeleteModal,
      handleShowCopyDialog,
      handleCloseCopyDialog,
      handleCopyDiagram,
      handleRowClick,
    },
  }
}
